import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Upload,
  FileSpreadsheet,
  Shield,
  Zap,
  ChevronRight,
  Headphones,
  Loader2,
  Send,
  Play,
  DollarSign,
  Download,
  Search,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import { analytics } from "./analytics";
import { API } from "./apiConfig";
import { toast } from "sonner";

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [msg, setMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/marketing/subscribe`, { email, source: "homepage_footer" });
      setStatus("success");
      setMsg(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMsg(err.response?.data?.error || "Something went wrong. Try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 max-w-md mx-auto" data-testid="newsletter-form">
      <input
        type="email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
        placeholder="you@business.com"
        className="flex-1 px-4 py-2.5 rounded-lg bg-[#112A4A] border border-[#1C3D66] text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#F5D300]/50"
        data-testid="newsletter-email-input"
        required
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="bg-[#F5D300] text-black font-bold rounded-lg px-5 py-2.5 text-sm hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
        data-testid="newsletter-submit-btn"
      >
        {status === "loading" ? "..." : "Subscribe"}
      </button>
      {status === "success" && <span className="self-center text-xs text-emerald-400 ml-2">{msg}</span>}
      {status === "error" && <span className="self-center text-xs text-red-400 ml-2">{msg}</span>}
    </form>
  );
}

export default function LandingPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [wgForm, setWgForm] = useState({ name: "", email: "", business: "", employees: "" });
  const [wgSubmitted, setWgSubmitted] = useState(false);
  const [wgLoading, setWgLoading] = useState(false);

  // Capture Clover OAuth redirect: ?code=...&merchant_id=...
  useEffect(() => {
    const code = searchParams.get("code");
    const merchantId = searchParams.get("merchant_id");
    if (code && merchantId) {
      // Strip OAuth params from URL immediately
      window.history.replaceState({}, "", "/");
      handleCloverOAuth(code, merchantId);
    }
  }, []);

  async function handleCloverOAuth(code, merchantId) {
    try {
      const { data } = await axios.post(`${API}/clover/oauth/exchange`, {
        code, merchant_id: merchantId,
      }, { withCredentials: true });

      if (data.status === "logged_in") {
        await checkAuth();
        toast.success("Connected to Clover", { description: `Merchant: ${data.user?.merchant_name || merchantId}` });
        navigate("/tool");
      } else if (data.status === "needs_registration") {
        sessionStorage.setItem("pending_clover_token", data.pending_token);
        sessionStorage.setItem("clover_merchant_id", data.merchant_id);
        sessionStorage.setItem("clover_merchant_name", data.merchant_name || "");
        toast.info("Almost there!", { description: "Create an account to finish connecting your Clover." });
        navigate("/auth?mode=register&from=clover");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error("Clover connection failed", { description: detail });
    }
  }
  const [activeDemo, setActiveDemo] = useState(0);

  const demos = [
    { key: "upload", label: "Upload & Process", icon: <Upload size={16} />, gif: "/demos/upload_process.gif", desc: "Drag your Clover CSV, enter tips, select your state — done in seconds." },
    { key: "review", label: "Shift Review", icon: <Search size={16} />, gif: "/demos/shift_review.gif", desc: "Edit clock times, flag sick days, override overtime — full control before export." },
    { key: "labor", label: "Labor Cost", icon: <DollarSign size={16} />, gif: "/demos/labor_cost.gif", desc: "Set wages, assign departments, and see total labor costs calculated instantly." },
    { key: "download", label: "Excel Download", icon: <Download size={16} />, gif: "/demos/excel_download.gif", desc: "One click to a formatted, color-coded Excel — ready for your payroll provider." },
  ];

  return (
    <div className="min-h-screen bg-[#0A1F3B]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0A1F3B]/80 backdrop-blur-xl border-b border-[#1C3D66]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1" data-testid="nav-logo">
            <span className="font-heading text-2xl font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-2xl font-black text-white">Payroll</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              className="text-[#94A3B8] hover:text-white text-sm font-medium transition-colors"
              data-testid="nav-blog"
            >
              Blog
            </Link>
            {user ? (
              <Link
                to="/tool"
                className="bg-[#F5D300] text-black font-bold rounded-lg px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
                data-testid="nav-go-to-tool"
              >
                Open Tool
              </Link>
            ) : (
              <>
                <Link
                  to="/auth"
                  className="text-[#94A3B8] hover:text-white text-sm font-medium transition-colors"
                  data-testid="nav-login"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth?mode=register"
                  className="bg-[#F5D300] text-black font-bold rounded-lg px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
                  data-testid="nav-signup"
                >
                  Start Free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 sm:py-28 px-6 sm:px-8" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05] mb-5">
              Turn your Clover shifts into payroll-ready reports in under
              <span className="text-[#F5D300]"> 5 minutes.</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-xl">
              No spreadsheets. No manual cleanup. Just clean exports ready for ADP, Gusto, QuickBooks, Paychex, or your accountant.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/tool"
                className="inline-flex items-center gap-2 bg-[#F5D300] text-black font-bold rounded-lg px-6 py-3.5 text-base hover:-translate-y-1 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
                data-testid="hero-cta"
              >
                Run Your First Payroll Free
                <ArrowRight size={18} />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 bg-[#1C3D66] text-white font-semibold rounded-lg px-6 py-3.5 text-base hover:bg-[#1C3D66]/80 transition-colors"
                data-testid="hero-secondary-cta"
              >
                See How It Works
              </a>
            </div>
            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-4 mt-5" data-testid="hero-trust-badges">
              <span className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8]">
                <ShieldCheck size={14} className="text-green-400" />
                No signup required
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8]">
                <Shield size={14} className="text-green-400" />
                No credit card needed
              </span>
            </div>
          </div>

          {/* Before/After Visual */}
          <div className="mt-16 grid md:grid-cols-2 gap-6" data-testid="before-after-visual">
            {/* Before */}
            <div className="bg-[#112A4A] border border-[#1C3D66] rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#1C3D66]">
                <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                <span className="text-xs text-[#94A3B8] ml-2 font-mono">clover_shifts_export.csv</span>
              </div>
              <div className="p-5 font-mono text-xs leading-relaxed">
                <span className="text-red-400">SHIFTS,,,,,,,,</span>
                <br />
                <span className="text-[#64748B]">Employee ID,Employee Name,Custom ID,</span>
                <br />
                <span className="text-[#94A3B8]">1001,Martinez Ana,,14-Feb-2026,07:02:33 AM,</span>
                <br />
                <span className="text-[#94A3B8]">14-Feb-2026,</span>
                <span className="text-orange-400">03:15:01 PM</span>
                <span className="text-[#94A3B8]">,8.21</span>
                <br />
                <span className="text-[#94A3B8]">1001,Martinez Ana,,14-Feb-2026,</span>
                <span className="text-red-400">11:58:00 PM</span>
                <span className="text-[#94A3B8]">,</span>
                <br />
                <span className="text-red-400">15-Feb-2026,07:01:22 AM,7.06</span>
                <br />
                <span className="text-yellow-400">// forgot to clock out?</span>
                <br />
                <span className="text-[#94A3B8]">1003,Johnson Kea,,14-Feb-2026,06:55:12 AM</span>
                <br />
                <span className="text-[#475569]">... 180 more rows of this mess</span>
              </div>
              <div className="px-4 py-2.5 bg-red-500/10 border-t border-red-500/20">
                <span className="text-red-400 text-xs font-semibold">BEFORE: Raw Clover CSV export</span>
              </div>
            </div>

            {/* After */}
            <div className="bg-[#112A4A] border border-[#F5D300]/30 rounded-xl overflow-hidden shadow-[0_0_30px_-12px_rgba(245,211,0,0.15)]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F5D300]/20">
                <FileSpreadsheet size={14} className="text-[#F5D300]" />
                <span className="text-xs text-[#F5D300] font-semibold">shifts 2.14.26 to 2.20.26.xlsx</span>
                <span className="ml-auto text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">READY</span>
              </div>
              <div className="p-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[#94A3B8] border-b border-[#1C3D66]">
                      <th className="text-left py-2 font-semibold">Employee</th>
                      <th className="text-right py-2 font-semibold">Total</th>
                      <th className="text-right py-2 font-semibold">Regular</th>
                      <th className="text-right py-2 font-semibold">OT</th>
                    </tr>
                  </thead>
                  <tbody className="text-white">
                    <tr className="border-b border-[#1C3D66]/50">
                      <td className="py-2 font-medium">Martinez, Ana</td>
                      <td className="text-right font-mono text-[#F5D300]">38.45</td>
                      <td className="text-right font-mono">32.00</td>
                      <td className="text-right font-mono text-orange-400">6.45</td>
                    </tr>
                    <tr className="border-b border-[#1C3D66]/50">
                      <td className="py-2 font-medium">Johnson, Kea</td>
                      <td className="text-right font-mono text-[#F5D300]">42.12</td>
                      <td className="text-right font-mono">40.00</td>
                      <td className="text-right font-mono text-orange-400">2.12</td>
                    </tr>
                    <tr className="border-b border-[#1C3D66]/50">
                      <td className="py-2 font-medium">Lee, Sarah</td>
                      <td className="text-right font-mono text-[#F5D300]">24.00</td>
                      <td className="text-right font-mono">24.00</td>
                      <td className="text-right font-mono text-[#475569]">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2.5 bg-[#F5D300]/10 border-t border-[#F5D300]/20">
                <span className="text-[#F5D300] text-xs font-semibold">AFTER: Clean, payroll-ready Excel</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-3">How it works</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-14">
            Three steps. Under five minutes.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Upload size={24} />, step: "01", title: "Upload your CSV", desc: "Export shifts from Clover and drag the file here. That's it.", gif: "/demos/upload_process.gif" },
              { icon: <Zap size={24} />, step: "02", title: "We clean it up", desc: "Overtime calculated. Break violations flagged. Clock-out errors fixed automatically.", gif: "/demos/shift_review.gif" },
              { icon: <FileSpreadsheet size={24} />, step: "03", title: "Download your report", desc: "Get a formatted Excel file ready for ADP, Gusto, QuickBooks, Paychex, or your accountant.", gif: "/demos/excel_download.gif" },
            ].map((item, i) => (
              <div key={i} className="group relative bg-[#112A4A] border border-[#1C3D66] rounded-xl overflow-hidden hover:border-[#F5D300]/30 transition-all duration-300" data-testid={`how-step-${item.step}`}>
                <div className="p-6 relative">
                  <div className="text-[80px] font-heading font-black text-[#1C3D66]/40 absolute top-2 right-4 leading-none select-none">
                    {item.step}
                  </div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-[#F5D300]/10 rounded-lg flex items-center justify-center text-[#F5D300] mb-5">
                      {item.icon}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <div className="rounded-lg overflow-hidden border border-[#1C3D66]/60 bg-[#0A1F3B]">
                    <img
                      src={item.gif}
                      alt={item.title}
                      className="w-full h-auto opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See It In Action */}
      <section id="see-it" className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="demo-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F5D300]/10 border border-[#F5D300]/20 rounded-full px-4 py-1.5 mb-4">
                <Play size={12} className="text-[#F5D300]" />
                <span className="text-[#F5D300] text-xs font-bold uppercase tracking-widest">Live demos</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
                See it in action.
              </h2>
            </div>
            {/* Tab pills */}
            <div className="flex flex-wrap gap-2" data-testid="demo-tabs">
              {demos.map((d, i) => (
                <button
                  key={d.key}
                  onClick={() => setActiveDemo(i)}
                  data-testid={`demo-tab-${d.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                    activeDemo === i
                      ? "bg-[#F5D300] text-black shadow-[0_4px_12px_-2px_rgba(245,211,0,0.3)]"
                      : "bg-[#112A4A] text-[#94A3B8] border border-[#1C3D66] hover:border-[#F5D300]/30 hover:text-white"
                  }`}
                >
                  {d.icon}
                  <span className="hidden sm:inline">{d.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Demo viewer */}
          <div className="grid lg:grid-cols-12 gap-6 items-start">
            {/* GIF display */}
            <div className="lg:col-span-8">
              <div className="rounded-xl overflow-hidden border border-[#1C3D66] bg-[#0A1F3B] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]" data-testid="demo-viewer">
                {/* Fake browser bar */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1C3D66] bg-[#0A1F3B]">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/50"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/50"></div>
                  <span className="text-[10px] text-[#475569] font-mono ml-2">olypayroll.com/tool</span>
                </div>
                <img
                  key={demos[activeDemo].key}
                  src={demos[activeDemo].gif}
                  alt={demos[activeDemo].label}
                  className="w-full h-auto"
                  data-testid="demo-gif-img"
                />
              </div>
            </div>

            {/* Description sidebar */}
            <div className="lg:col-span-4 flex flex-col gap-3">
              {demos.map((d, i) => (
                <button
                  key={d.key}
                  onClick={() => setActiveDemo(i)}
                  data-testid={`demo-card-${d.key}`}
                  className={`text-left rounded-xl p-5 transition-all duration-200 border ${
                    activeDemo === i
                      ? "bg-[#112A4A] border-[#F5D300]/40 shadow-[0_0_20px_-8px_rgba(245,211,0,0.15)]"
                      : "bg-[#0A1F3B]/50 border-[#1C3D66]/50 hover:bg-[#112A4A]/60 hover:border-[#1C3D66]"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={activeDemo === i ? "text-[#F5D300]" : "text-[#475569]"}>{d.icon}</span>
                    <h4 className={`font-heading text-sm font-bold ${activeDemo === i ? "text-white" : "text-[#94A3B8]"}`}>
                      {d.label}
                    </h4>
                  </div>
                  <p className={`text-xs leading-relaxed ${activeDemo === i ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                    {d.desc}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="value-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-3">Why oly Payroll</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            This pays for itself in one run.
          </h2>
          <p className="text-[#94A3B8] text-base mb-12 max-w-lg">
            Manual payroll prep takes 45-60 minutes. oly Payroll does it in 30 seconds.
            Even at $19/month, you're paying less than the cost of 15 minutes of your time.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <Clock size={20} />, text: "Turn messy exports into clean reports instantly" },
              { icon: <CheckCircle size={20} />, text: "Overtime calculated automatically" },
              { icon: <FileSpreadsheet size={20} />, text: "Ready for ADP, Gusto, QuickBooks & Paychex" },
              { icon: <Shield size={20} />, text: "No setup, no training, no spreadsheets" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#112A4A] border border-[#1C3D66] rounded-xl p-5">
                <div className="text-[#F5D300] mt-0.5 flex-shrink-0">{item.icon}</div>
                <span className="text-sm text-white font-medium leading-snug">{item.text}</span>
              </div>
            ))}
          </div>

          {/* ROI Box */}
          <div className="mt-12 bg-[#112A4A] border border-[#F5D300]/20 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Time comparison</p>
              <div className="flex items-end gap-6 mb-3">
                <div>
                  <p className="text-sm text-[#94A3B8]">Manual prep</p>
                  <p className="font-heading text-4xl font-black text-red-400">~45 min</p>
                </div>
                <div className="text-[#475569] text-2xl pb-1">vs</div>
                <div>
                  <p className="text-sm text-[#94A3B8]">oly Payroll</p>
                  <p className="font-heading text-4xl font-black text-[#F5D300]">~30 sec</p>
                </div>
              </div>
            </div>
            <Link
              to="/tool"
              className="inline-flex items-center gap-2 bg-[#F5D300] text-black font-bold rounded-lg px-6 py-3.5 hover:-translate-y-1 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300 whitespace-nowrap"
            >
              Try It Free <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-6">
          <div className="md:col-span-8 bg-[#112A4A] border border-[#1C3D66] rounded-2xl p-8 flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-4">Built for real businesses</p>
            <blockquote className="font-heading text-2xl sm:text-3xl font-bold text-white leading-snug mb-4">
              &ldquo;I got tired of the spreadsheet nightmare, so I built this.&rdquo;
            </blockquote>
            <p className="text-[#94A3B8] text-base leading-relaxed">
              Built by a business owner using Clover daily. We know the pain because we lived it.
              oly Payroll was made to solve a real problem, not check a feature box.
            </p>
          </div>
          <div className="md:col-span-4 rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1601933973783-43cf8a7d4c5f?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Business owner working on payroll"
              className="w-full h-full object-cover min-h-[280px]"
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-3">Pricing</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
              Cut your Clover payroll prep time by 50-80%
            </h2>
            <p className="text-[#94A3B8] text-base max-w-md mx-auto">
              Your first payroll run is 100% free. No signup required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {/* Pay As You Go */}
            <div className="bg-[#112A4A] border border-[#1C3D66] rounded-2xl p-7" data-testid="pricing-paygo-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Pay as you go</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-4xl font-black text-white">$5</span>
                <span className="text-[#94A3B8] text-sm">/ export</span>
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">Best for occasional use or trying it out</p>
              <ul className="space-y-3 mb-7">
                {["Full payroll processing", "Clean export files", "Overtime calculations", "Sick hours tracking"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-white">
                    <CheckCircle size={16} className="text-[#F5D300] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/tool"
                className="block text-center bg-[#1C3D66] text-white font-semibold rounded-lg px-6 py-3 hover:bg-[#1C3D66]/80 transition-colors"
                data-testid="pricing-paygo-cta"
              >
                Run Payroll for $5
              </Link>
            </div>

            {/* Unlimited */}
            <div className="relative bg-[#112A4A] border-2 border-[#F5D300]/40 rounded-2xl p-7 shadow-[0_0_40px_-12px_rgba(245,211,0,0.12)] md:scale-[1.04]" data-testid="pricing-unlimited-card">
              <div className="absolute -top-3 left-6 bg-[#F5D300] text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                Most popular
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-2">Unlimited</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-4xl font-black text-white">$19</span>
                <span className="text-[#94A3B8] text-sm">/ month</span>
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">Best for weekly payroll users</p>
              <p className="text-[10px] font-semibold text-[#F5D300]/70 uppercase tracking-wide mb-3">Everything in Pay As You Go, plus:</p>
              <ul className="space-y-3 mb-7">
                {["Unlimited payroll runs", "Save & access past reports", "Custom employee & tip settings", "Fixed hour employee management", "Labor cost tracking & department analytics", "Historical report comparison", "Priority support"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-white">
                    <CheckCircle size={16} className="text-[#F5D300] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/auth?mode=register"
                className="block text-center bg-[#F5D300] text-black font-bold rounded-lg px-6 py-3 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
                data-testid="pricing-unlimited-cta"
              >
                Start Unlimited Plan
              </Link>
              <p className="text-[10px] text-[#94A3B8] text-center mt-3">
                Most Clover businesses choose this
              </p>
            </div>

            {/* White-Glove */}
            <div className="bg-[#112A4A] border border-[#1C3D66] rounded-2xl p-7" data-testid="pricing-whiteglove-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-2">White-Glove</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-4xl font-black text-white">$99</span>
                <span className="text-[#94A3B8] text-sm">/ month</span>
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">We do the upload and produce the finished report for you</p>
              <p className="text-[10px] font-semibold text-[#94A3B8]/70 uppercase tracking-wide mb-3">Everything in Unlimited, plus:</p>
              <ul className="space-y-3 mb-7">
                {["We handle the CSV upload for you", "Reviewed & verified report", "Delivered to your inbox weekly", "Dedicated account support"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-white">
                    <CheckCircle size={16} className="text-[#F5D300] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#white-glove"
                className="block text-center bg-[#1C3D66] text-white font-semibold rounded-lg px-6 py-3 hover:bg-[#1C3D66]/80 transition-colors"
                data-testid="pricing-whiteglove-cta"
              >
                <Headphones size={16} className="inline mr-1.5 -mt-0.5" />
                We&apos;ll Set This Up for You
              </a>
            </div>
          </div>

          <div className="text-center mt-8 space-y-1">
            <p className="text-sm text-[#94A3B8]">No contracts. Cancel anytime. Secure data handling.</p>
          </div>

          {/* Feature Comparison Table */}
          <div className="mt-16 max-w-4xl mx-auto" data-testid="pricing-comparison-table">
            <h3 className="font-heading text-xl font-bold text-white mb-6 text-center">Compare plans</h3>
            <div className="bg-[#112A4A] border border-[#1C3D66] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1C3D66]">
                    <th className="text-left py-3 px-5 text-[#94A3B8] font-semibold">Feature</th>
                    <th className="text-center py-3 px-3 text-[#94A3B8] font-semibold">Free</th>
                    <th className="text-center py-3 px-3 text-[#F5D300] font-semibold">Pay As You Go</th>
                    <th className="text-center py-3 px-3 text-[#F5D300] font-semibold">Unlimited</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "Process Clover CSVs", free: true, paygo: true, unlimited: true },
                    { feature: "Overtime calculations", free: true, paygo: true, unlimited: true },
                    { feature: "Break violation flagging", free: true, paygo: true, unlimited: true },
                    { feature: "Excel download", free: "1 free", paygo: true, unlimited: true },
                    { feature: "Tip distribution", free: true, paygo: true, unlimited: true },
                    { feature: "Past report history", free: false, paygo: false, unlimited: true },
                    { feature: "Custom employee settings", free: false, paygo: false, unlimited: true },
                    { feature: "Labor cost tracking", free: false, paygo: false, unlimited: true },
                    { feature: "Historical comparison", free: false, paygo: false, unlimited: true },
                    { feature: "Fixed hour employees", free: false, paygo: false, unlimited: true },
                    { feature: "Priority support", free: false, paygo: false, unlimited: true },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-[#0A1F3B]/30" : ""}>
                      <td className="py-2.5 px-5 text-white font-medium">{row.feature}</td>
                      <td className="text-center py-2.5 px-3">
                        {row.free === true ? (
                          <CheckCircle size={16} className="text-green-400 mx-auto" />
                        ) : row.free === false ? (
                          <span className="text-[#475569]">—</span>
                        ) : (
                          <span className="text-xs text-[#F5D300] font-semibold">{row.free}</span>
                        )}
                      </td>
                      <td className="text-center py-2.5 px-3">
                        {row.paygo === true ? (
                          <CheckCircle size={16} className="text-green-400 mx-auto" />
                        ) : row.paygo === false ? (
                          <span className="text-[#475569]">—</span>
                        ) : (
                          <span className="text-xs text-[#F5D300] font-semibold">{row.paygo}</span>
                        )}
                      </td>
                      <td className="text-center py-2.5 px-3">
                        {row.unlimited === true ? (
                          <CheckCircle size={16} className="text-[#F5D300] mx-auto" />
                        ) : (
                          <span className="text-[#475569]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* White-Glove Lead Form */}
      <section id="white-glove" className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="whiteglove-section">
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#112A4A] border border-[#1C3D66] rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F5D300]/10 rounded-lg flex items-center justify-center">
                <Headphones size={20} className="text-[#F5D300]" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-white">White-Glove Payroll Service</h2>
                <p className="text-sm text-[#94A3B8]">$99/month — We handle everything for you</p>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] leading-relaxed mb-6">
              Set us up with bookkeeper credentials in your Clover account, and we&apos;ll deliver a reviewed, 
              payroll-ready report straight to your inbox every week. No software to learn. No buttons to click.
            </p>

            {wgSubmitted ? (
              <div className="text-center py-8" data-testid="whiteglove-success">
                <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
                <p className="font-heading text-lg font-bold text-white mb-1">We&apos;ll be in touch!</p>
                <p className="text-sm text-[#94A3B8]">Expect a response within 24 hours.</p>
              </div>
            ) : (
              <form
                data-testid="whiteglove-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setWgLoading(true);
                  try {
                    await axios.post(`${API}/leads/white-glove`, wgForm);
                  } catch {
                    // Still show success even if API not ready yet
                  }
                  setWgSubmitted(true);
                  setWgLoading(false);
                }}
                className="space-y-3"
              >
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    data-testid="wg-name-input"
                    type="text"
                    placeholder="Your name"
                    required
                    value={wgForm.name}
                    onChange={(e) => setWgForm({ ...wgForm, name: e.target.value })}
                    className="w-full h-10 bg-[#0A1F3B] border border-[#1C3D66] rounded-lg px-4 text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#F5D300] focus:ring-1 focus:ring-[#F5D300] transition-colors"
                  />
                  <input
                    data-testid="wg-email-input"
                    type="email"
                    placeholder="Email address"
                    required
                    value={wgForm.email}
                    onChange={(e) => setWgForm({ ...wgForm, email: e.target.value })}
                    className="w-full h-10 bg-[#0A1F3B] border border-[#1C3D66] rounded-lg px-4 text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#F5D300] focus:ring-1 focus:ring-[#F5D300] transition-colors"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    data-testid="wg-business-input"
                    type="text"
                    placeholder="Business name"
                    value={wgForm.business}
                    onChange={(e) => setWgForm({ ...wgForm, business: e.target.value })}
                    className="w-full h-10 bg-[#0A1F3B] border border-[#1C3D66] rounded-lg px-4 text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#F5D300] focus:ring-1 focus:ring-[#F5D300] transition-colors"
                  />
                  <input
                    data-testid="wg-employees-input"
                    type="text"
                    placeholder="# of employees"
                    value={wgForm.employees}
                    onChange={(e) => setWgForm({ ...wgForm, employees: e.target.value })}
                    className="w-full h-10 bg-[#0A1F3B] border border-[#1C3D66] rounded-lg px-4 text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[#F5D300] focus:ring-1 focus:ring-[#F5D300] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={wgLoading}
                  className="w-full h-11 bg-[#F5D300] text-black font-bold rounded-lg flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300 disabled:opacity-50"
                  data-testid="wg-submit-btn"
                >
                  {wgLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Get Started with White-Glove
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section - Great for SEO */}
      <section id="faq" className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5D300] mb-3">FAQ</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-10">
            Common questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "How does oly Payroll work with Clover POS?",
                a: "Export your shifts from Clover POS as a CSV file, upload it to oly Payroll, and instantly receive a clean, payroll-ready Excel report with overtime calculated, break violations flagged, and anomalies detected.",
              },
              {
                q: "Is oly Payroll compatible with ADP, Gusto, QuickBooks, and Paychex?",
                a: "Yes. oly Payroll generates clean, formatted Excel files that can be imported into ADP, Gusto, QuickBooks, Paychex, or sent directly to your accountant.",
              },
              {
                q: "How much does oly Payroll cost?",
                a: "Your first payroll run is 100% free with no signup required. After that, choose Pay As You Go at $5 per export, or Unlimited at $19/month for unlimited payroll runs plus premium features like custom employee settings.",
              },
              {
                q: "Does oly Payroll calculate overtime automatically?",
                a: "Yes. oly Payroll automatically calculates daily overtime (shifts over 8 hours) and weekly overtime (more than 5 days worked), and flags break violations for shifts over 6 hours.",
              },
              {
                q: "Do I need to create an account to try it?",
                a: "No. Your first payroll run is completely free with no account required. Just upload your Clover CSV and see the results instantly.",
              },
            ].map((item, i) => (
              <details key={i} className="group bg-[#112A4A] border border-[#1C3D66] rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none text-white font-semibold text-sm hover:bg-[#1A365D] transition-colors">
                  {item.q}
                  <ChevronRight size={16} className="text-[#94A3B8] transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-4 text-sm text-[#94A3B8] leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 px-6 sm:px-8 border-t border-[#1C3D66]" data-testid="final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
            Stop spending your time on payroll prep.
          </h2>
          <p className="text-[#94A3B8] text-base mb-8">
            Upload your Clover shifts and get a clean, payroll-ready report in under 5 minutes.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/tool"
              className="inline-flex items-center gap-2 bg-[#F5D300] text-black font-bold rounded-lg px-8 py-4 text-base hover:-translate-y-1 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
              data-testid="final-cta-btn"
            >
              Run Your First Payroll Free <ArrowRight size={18} />
            </Link>
          </div>
          <p className="text-xs text-[#94A3B8] mt-4">
            No signup required for your first run. No credit card needed.
          </p>
        </div>
      </section>

      {/* Newsletter + Footer */}
      <footer className="border-t border-[#1C3D66] py-12 px-6 sm:px-8" data-testid="footer-section">
        <div className="max-w-7xl mx-auto">
          {/* Newsletter signup */}
          <div className="max-w-xl mx-auto text-center mb-10">
            <h3 className="font-heading text-lg font-bold text-white mb-2">Get payroll tips in your inbox</h3>
            <p className="text-sm text-[#94A3B8] mb-4">
              Weekly advice on overtime rules, payroll prep, and compliance updates. No spam, unsubscribe anytime.
            </p>
            <NewsletterSignup />
          </div>
          <div className="border-t border-[#1C3D66] pt-6 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="font-heading text-lg font-light text-[#F5D300]">oly</span>
              <span className="font-heading text-lg font-black text-white">Payroll</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#475569]">
              <Link to="/blog" className="hover:text-[#94A3B8]" data-testid="footer-blog">Blog</Link>
              <Link to="/privacy" className="hover:text-[#94A3B8]" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#94A3B8]" data-testid="footer-terms">Terms of Use</Link>
            </div>
          </div>
          <p className="text-[10px] text-[#334155] leading-relaxed mt-4">
            Clover&reg; is a registered trademark of Fiserv, Inc. or its affiliates. ADP&reg; is a registered trademark of ADP, Inc. 
            QuickBooks&reg; is a registered trademark of Intuit Inc. Gusto&reg; is a registered trademark of Gusto, Inc. 
            Paychex&reg; is a registered trademark of Paychex, Inc. 
            oly Payroll is not affiliated with, endorsed by, or sponsored by any of these companies. 
            All trademarks belong to their respective owners.
          </p>
          <p className="text-[10px] text-[#334155] leading-relaxed mt-2">
            By subscribing you agree to receive marketing emails from Oly Payroll. You can unsubscribe at any time by clicking the link in the email footer.
            We respect your privacy and will never share your email address. CAN-SPAM compliant.
          </p>
        </div>
      </footer>
    </div>
  );
}
